/**
 * pages/Folders.tsx
 * Full folder management page.
 * Create, rename, colour-pick, delete folders and sub-folders.
 * Clicking a folder navigates to Dashboard filtered to that folder.
 * Awaits: foldersApi (backend wired via store — flip MOCK_MODE=false)
 */

import React, { useEffect, useState } from 'react';
import {
  Folder, FolderOpen, Plus, Trash2, Edit2, Check, X,
  ChevronRight, ArrowLeft, FileText,
} from 'lucide-react';
import { useStore } from '../store';
import { useAuth } from '../hooks';
import { GlobalSpin, Spinner, EmptyState } from '../components/ui';

const C = {
  amber100:'#FFF4E0', amber200:'#FFD88A', amber300:'#F5A623',
  amber400:'#C4811A', amber500:'#8B5A0A',
  ink:'#1A1814', inkLight:'#3D3A34', muted:'#7A7570',
  border:'#E8E4DC', surface:'#FAFAF8', white:'#FFFFFF', red:'#DC2626',
};
const font = { head:"'Sora',sans-serif", body:"'DM Sans',sans-serif" };

const COLOUR_PALETTE = [
  '#F5A623','#C4811A','#16A34A','#7C3AED',
  '#DB2777','#2563EB','#0891B2','#DC2626',
  '#D97706','#059669','#9333EA','#E11D48',
];

// ─── Colour Picker ────────────────────────────────────────────────────────────
const ColourPicker: React.FC<{ value: string; onChange: (c: string) => void }> = ({ value, onChange }) => (
  <div style={{ display:'flex', flexWrap:'wrap', gap:6, padding:'10px 0' }}>
    {COLOUR_PALETTE.map(c => (
      <button key={c} onClick={() => onChange(c)} style={{
        width:24, height:24, borderRadius:6, background:c, border:`2.5px solid ${c === value ? C.ink : 'transparent'}`,
        cursor:'pointer', transition:'transform .1s', outline:'none',
        transform: c === value ? 'scale(1.15)' : 'scale(1)',
      }}/>
    ))}
  </div>
);

// ─── New Folder Form ──────────────────────────────────────────────────────────
const NewFolderForm: React.FC<{ parentId?: string | null; onDone: () => void }> = ({ parentId = null, onDone }) => {
  const createFolder = useStore(s => s.createFolder);
  const [name, setName]     = useState('');
  const [colour, setColour] = useState(COLOUR_PALETTE[0]);
  const [saving, setSaving] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    await createFolder(name.trim(), colour, parentId);
    setSaving(false);
    onDone();
  };

  return (
    <form onSubmit={submit} style={{ background:C.white, border:`1.5px solid ${C.amber300}`, borderRadius:14, padding:'18px 20px', marginBottom:10 }}>
      <p style={{ fontSize:12, fontWeight:700, color:C.amber500, fontFamily:font.body, marginBottom:10, textTransform:'uppercase', letterSpacing:'0.06em' }}>
        {parentId ? 'New sub-folder' : 'New folder'}
      </p>
      <input
        autoFocus value={name} onChange={e => setName(e.target.value)}
        placeholder="Folder name" maxLength={40}
        style={{ width:'100%', height:40, padding:'0 12px', borderRadius:8, border:`1.5px solid ${C.border}`, fontFamily:font.body, fontSize:14, color:C.ink, outline:'none', marginBottom:8 }}
      />
      <ColourPicker value={colour} onChange={setColour}/>
      <div style={{ display:'flex', gap:8, marginTop:10 }}>
        <button type="submit" disabled={!name.trim() || saving} style={{
          height:36, padding:'0 16px', borderRadius:8, border:'none', cursor:'pointer',
          background:`linear-gradient(135deg,${C.amber300},${C.amber400})`, color:C.white,
          fontWeight:700, fontSize:13, fontFamily:font.body, opacity: saving ? 0.7 : 1,
        }}>
          {saving ? 'Creating…' : <><Check size={13} style={{ display:'inline', marginRight:4 }}/>Create</>}
        </button>
        <button type="button" onClick={onDone} style={{ height:36, padding:'0 14px', borderRadius:8, border:`1.5px solid ${C.border}`, background:'none', cursor:'pointer', color:C.muted, fontSize:13, fontFamily:font.body }}>
          <X size={13} style={{ display:'inline', marginRight:4 }}/>Cancel
        </button>
      </div>
    </form>
  );
};

// ─── Folder Row ───────────────────────────────────────────────────────────────
interface FolderRowProps {
  folder: import('../types').Folder;
  depth: number;
  children: import('../types').Folder[];
}
const FolderRow: React.FC<FolderRowProps> = ({ folder, depth, children }) => {
  const updateFolder = useStore(s => s.updateFolder);
  const deleteFolder = useStore(s => s.deleteFolder);
  const notes        = useStore(s => s.notes);

  const [expanded, setExpanded]   = useState(false);
  const [editing, setEditing]     = useState(false);
  const [editName, setEditName]   = useState(folder.name);
  const [editColour, setEditColour] = useState(folder.color);
  const [showNewSub, setShowNewSub] = useState(false);
  const [hovered, setHovered]     = useState(false);

  const noteCount = notes.filter(n => n.folderId === folder.id).length;

  const saveEdit = async () => {
    if (editName.trim()) await updateFolder(folder.id, { name: editName.trim(), color: editColour });
    setEditing(false);
  };

  const goToFolder = () => { window.location.href = `/dashboard?folder=${folder.id}`; };

  return (
    <div>
      <div
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          display:'flex', alignItems:'center', gap:10, padding:`11px ${16 + depth * 20}px`,
          borderRadius:12, background: hovered ? C.amber100 : C.white,
          border:`1px solid ${hovered ? C.amber200 : C.border}`,
          marginBottom:6, transition:'all .12s', cursor:'pointer',
        }}
      >
        {/* Expand arrow */}
        {children.length > 0 ? (
          <button onClick={() => setExpanded(s => !s)} style={{ background:'none', border:'none', cursor:'pointer', color:C.muted, padding:2, display:'flex', flexShrink:0, transform: expanded ? 'rotate(90deg)' : 'rotate(0)', transition:'transform .15s' }}>
            <ChevronRight size={14}/>
          </button>
        ) : (
          <div style={{ width:18, flexShrink:0 }}/>
        )}

        {/* Folder icon */}
        <div onClick={goToFolder} style={{ flexShrink:0 }}>
          {expanded ? <FolderOpen size={18} color={folder.color}/> : <Folder size={18} color={folder.color}/>}
        </div>

        {/* Name / edit */}
        {editing ? (
          <div style={{ flex:1 }}>
            <input autoFocus value={editName} onChange={e => setEditName(e.target.value)}
              onKeyDown={e => { if (e.key==='Enter') saveEdit(); if (e.key==='Escape') setEditing(false); }}
              style={{ width:'100%', height:32, padding:'0 10px', borderRadius:7, border:`1.5px solid ${C.amber300}`, fontFamily:font.body, fontSize:13, outline:'none' }}/>
            <ColourPicker value={editColour} onChange={setEditColour}/>
          </div>
        ) : (
          <div onClick={goToFolder} style={{ flex:1, display:'flex', alignItems:'center', gap:10, minWidth:0 }}>
            <span style={{ fontSize:14, fontWeight:600, color:C.ink, fontFamily:font.body, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{folder.name}</span>
            <div style={{ width:6, height:6, borderRadius:'50%', background:folder.color, flexShrink:0 }}/>
            <span style={{ fontSize:12, color:C.muted, fontFamily:font.body, flexShrink:0 }}>
              <FileText size={11} style={{ display:'inline', marginRight:3 }}/>{noteCount}
            </span>
          </div>
        )}

        {/* Actions */}
        <div style={{ display:'flex', gap:4, flexShrink:0, opacity: hovered ? 1 : 0, transition:'opacity .1s' }}>
          {editing ? (
            <>
              <button onClick={saveEdit} style={{ background:'none', border:'none', cursor:'pointer', color:C.amber400, padding:5, display:'flex', borderRadius:6 }}><Check size={14}/></button>
              <button onClick={() => setEditing(false)} style={{ background:'none', border:'none', cursor:'pointer', color:C.muted, padding:5, display:'flex', borderRadius:6 }}><X size={14}/></button>
            </>
          ) : (
            <>
              <button onClick={() => setShowNewSub(s=>!s)} title="Add sub-folder"
                style={{ background:'none', border:'none', cursor:'pointer', color:C.muted, padding:5, display:'flex', borderRadius:6 }}><Plus size={14}/></button>
              <button onClick={() => setEditing(true)} title="Rename"
                style={{ background:'none', border:'none', cursor:'pointer', color:C.muted, padding:5, display:'flex', borderRadius:6 }}><Edit2 size={14}/></button>
              <button onClick={() => deleteFolder(folder.id)} title="Delete folder"
                style={{ background:'none', border:'none', cursor:'pointer', color:C.red, padding:5, display:'flex', borderRadius:6 }}><Trash2 size={14}/></button>
            </>
          )}
        </div>
      </div>

      {/* New sub-folder form */}
      {showNewSub && (
        <div style={{ paddingLeft: (depth + 1) * 20 + 16 }}>
          <NewFolderForm parentId={folder.id} onDone={() => setShowNewSub(false)}/>
        </div>
      )}

      {/* Children */}
      {expanded && children.map(child => (
        <FolderRow key={child.id} folder={child} depth={depth + 1}
          children={useStore.getState().folders.filter(f => f.parentId === child.id)}/>
      ))}
    </div>
  );
};

// ─── PAGE ─────────────────────────────────────────────────────────────────────
const FoldersPage: React.FC = () => {
  const { authLoading, isAuthenticated } = useAuth();
  const folders       = useStore(s => s.folders);
  const foldersLoading= useStore(s => s.foldersLoading);
  const loadFolders   = useStore(s => s.loadFolders);
  const loadNotes     = useStore(s => s.loadNotes);

  const [showNew, setShowNew] = useState(false);

  useEffect(() => { loadFolders(); loadNotes(); }, []); // eslint-disable-line

  // Auth gate — after all hooks
  if (authLoading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'100vh', background:'#FAFAF8', flexDirection:'column', gap:16 }}>
      <div style={{ width:40, height:40, borderRadius:'50%', border:'3px solid #FAEEDA', borderTopColor:'#F5A623', animation:'spin .7s linear infinite' }}/>
      <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
    </div>
  );
  if (!isAuthenticated) { window.location.replace('/login'); return null; }

  const rootFolders  = folders.filter(f => !f.parentId);
  const childrenOf   = (id: string) => folders.filter(f => f.parentId === id);

  if (authLoading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'100vh' }}>
      <Spinner size={32}/>
    </div>
  );

  return (
    <>
      <GlobalSpin/>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700;800&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
        body{font-family:'DM Sans',sans-serif;background:${C.surface};color:${C.ink};}
        input{-webkit-font-smoothing:antialiased;}
      `}</style>

      <div style={{ minHeight:'100vh', background:C.surface }}>
        {/* Top bar */}
        <div style={{ background:C.white, borderBottom:`1px solid ${C.border}`, height:60, display:'flex', alignItems:'center', gap:14, padding:'0 24px' }}>
          <a href="/dashboard" style={{ display:'flex', alignItems:'center', gap:6, color:C.muted, textDecoration:'none', fontSize:13, fontFamily:font.body, fontWeight:500 }}>
            <ArrowLeft size={15}/> Back to notes
          </a>
          <div style={{ flex:1 }}/>
          <button onClick={() => setShowNew(s=>!s)} style={{
            display:'flex', alignItems:'center', gap:6, padding:'8px 16px', borderRadius:10,
            border:'none', cursor:'pointer', background:`linear-gradient(135deg,${C.amber300},${C.amber400})`,
            color:C.white, fontWeight:700, fontSize:13, fontFamily:font.body,
          }}>
            <Plus size={15}/> New folder
          </button>
        </div>

        <div style={{ maxWidth:680, margin:'0 auto', padding:'32px 24px 60px' }}>
          <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', marginBottom:24 }}>
            <h1 style={{ fontFamily:font.head, fontWeight:800, fontSize:28, letterSpacing:'-0.04em', color:C.ink }}>Folders</h1>
            <span style={{ fontSize:13, color:C.muted, fontFamily:font.body }}>{folders.length} folder{folders.length!==1?'s':''}</span>
          </div>

          {showNew && <NewFolderForm onDone={() => setShowNew(false)}/>}

          {foldersLoading ? <Spinner/> : rootFolders.length === 0 && !showNew ? (
            <EmptyState
              icon={<Folder size={22}/>}
              title="No folders yet"
              desc="Organise your notes into folders and sub-folders. Build a structure that matches how your mind works."
              action={
                <button onClick={() => setShowNew(true)} style={{ display:'flex', alignItems:'center', gap:6, padding:'10px 20px', borderRadius:10, border:'none', cursor:'pointer', background:`linear-gradient(135deg,${C.amber300},${C.amber400})`, color:C.white, fontWeight:700, fontSize:13, fontFamily:font.body }}>
                  <Plus size={14}/> Create first folder
                </button>
              }
            />
          ) : (
            <div>
              {rootFolders.map(f => (
                <FolderRow key={f.id} folder={f} depth={0} children={childrenOf(f.id)}/>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default FoldersPage;
